import React, { useState, useEffect } from "react";
import { FaEye, FaEdit, FaTimes, FaDownload, FaCheck, FaTimesCircle } from 'react-icons/fa';
import axios from "axios";
import { CiSearch } from "react-icons/ci";
import { FaCopy, FaFileCsv, FaFileExcel, FaFilePdf } from "react-icons/fa";
import * as XLSX from 'xlsx';
import jsPDF from "jspdf";
import "jspdf-autotable";
import { BaseUrl } from "../App";
import { Circles } from 'react-loader-spinner';
import { FadeLoader } from 'react-spinners';


// Serviceinformation Component
const Serviceinformation = () => {
  const [services, setServices] = useState([]);
  // const [loading, setLoading] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedMethods, setSelectedMethods] = useState([]);
  const [showAddonsModal, setShowAddonsModal] = useState(false);
  const [showMethodsModal, setShowMethodsModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedValue, setSelectedValue] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(""); // State for error message

  useEffect(() => {
    let timer;
    if (error) {
      timer = setTimeout(() => {
        setError("");
      }, 5000); // 5 seconds
    }
    return () => clearTimeout(timer);
  }, [error]);

  
  // Fetch service data on mount
  useEffect(() => {
    const fetchServicesData = async () => {
      setLoading(true);
      try {
        const response = await axios.post(
          `${BaseUrl}/backend/api/services.php`, // API endpoint
          {
            action: "all_services", // Fetch all services
            api_key: "1"
          }
        );
        if (response.data.status === "true") {
          setServices(response.data.response); // Save services data
        } else {
          console.error("Failed to load services.");
        }
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchServicesData();
  }, []);

  // Handle Addons button click
  const handleAddonsClick = (service) => {
    setSelectedService(service);
    setSelectedAddons(service.addons); // Assuming the response has addons
    setShowAddonsModal(true);
  };

  // Handle Method button click
  const handleMethodsClick = (service) => {
    setSelectedService(service);
    setSelectedMethods(service.methods); // Assuming the response has methods
    setShowMethodsModal(true);
  };


    const [expanded, setExpanded] = useState({});
  
    const toggleMore = (id) => {
      setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
    }

  // Close Addons Modal
  const closeAddonsModal = () => setShowAddonsModal(false);

  // Close Methods Modal
  const closeMethodsModal = () => setShowMethodsModal(false);

  // Handle Search Input Change
  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };
  // Handle Copy functionality (Copy table content to clipboard)
  const handleCopy = () => {
    const tableContent = filteredServices
      .map((service) => {
        return [
          service.service_id,
          service.service_title,
          service.service_description,
          service.addons.map((addon) => addon.addon_name).join(", "),
          service.methods.map((method) => method.method_title).join(", ")
        ].join("\t");
      })
      .join("\n");

    navigator.clipboard.writeText(tableContent).then(() => {
      alert("Service data copied to clipboard!");
    });
  };

  // Handle entries per page change
  const handleChange = (event) => {
    setEntriesPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1); // Reset to page 1 when entries per page change
  };

  // Handle pagination
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1)); 
  const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const handlePageChange = (page) => setCurrentPage(page);

  // Filter services based on search query
  const filteredServices = services.filter((service) => {
    const serviceTitle = service.service_title.toLowerCase();
    const serviceId = service.service_id.toString();
    return serviceTitle.includes(searchQuery) || serviceId.includes(searchQuery);
  });

  // Pagination logic
  const startIndex = (currentPage - 1) * entriesPerPage;
  const paginatedServices = filteredServices.slice(startIndex, startIndex + entriesPerPage);
  const totalPages = Math.ceil(filteredServices.length / entriesPerPage);

  // Export to Excel
  const handleExcelExport = () => {
    const columns = [
      "Service ID", 
      "Service Title", 
      "Description", 
      "Addons", 
      "Methods"
    ];
  
    const rows = services.map((service) => [
      service.service_id, 
      service.service_title, 
      service.service_description,
      service.addons.map((addon) => addon.addon_name).join(", "), 
      service.methods.map((method) => method.method_title).join(", ")
    ]);
  
    const ws = XLSX.utils.aoa_to_sheet([columns, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Services Data");
    XLSX.writeFile(wb, "services-data.xlsx");
  };
  
  // Export to PDF
  const handlePdfExport = () => {
    const doc = new jsPDF();
    const columns = ["Service ID", "Service Title", "Description", "Addons", "Methods"];
    const rows = services.map((service) => [
      service.service_id,
      service.service_title,
      service.service_description,
      service.addons.map((addon) => addon.addon_name).join(", "),
      service.methods.map((method) => method.method_title).join(", ")
    ]);

    doc.autoTable({
      head: [columns],
      body: rows,
      startY: 20,
      theme: "grid",
      margin: { horizontal: 10 },
      headStyles: {
        fillColor: [0, 123, 255],
        textColor: 255,
      },
    });
    doc.save("services-data.pdf");
  };

  // Export to CSV
  const handleCsvExport = () => {
    const csvData = services.map((service) => [
      service.service_id,
      service.service_title,
      service.service_description,
      service.addons.map((addon) => addon.addon_name).join(", "),
      service.methods.map((method) => method.method_title).join(", ")
    ]);
  
    const csvContent = `data:text/csv;charset=utf-8,${csvData.join("\n")}`;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "services-data.csv");
    link.click();
  };

  // Loading state
  // if (loading) return <p>Loading...</p>;

  return (
    <div className="relative min-h-screen">
    {/* Loading Indicator */}
      {loading && (
           <div className="fixed inset-0 flex items-center justify-center z-50">
             <div className="flex flex-col items-center p-3 bg-black border border-white rounded-2xl">
               <FadeLoader 
                 height={15}
                 width={5}
                 color="#ffffff"
                 ariaLabel="loading"
               />
               <h1 className="mt-1 text-lg text-white">Please wait...</h1>
             </div>
           </div>
         )}
         
    <div className="container mx-auto px-4 md:px-6 lg:px-8  xl:px-24 py-6">
      <h1 className="text-3xl font-semibold text-blue-600 mb-6">Service Information</h1>
      <div className="p-6 font-sans">

  {/* Search and Export Options Section */}
  <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-6">
    {/* Search Bar */}
    <div className="flex items-center w-full sm:w-auto">
      <label className="block text-lg font-medium mb-2 sm:mb-0">
        Search:
        <input
          type="search"
          placeholder="Search by Name, Email or Phone"
          className="mt-2 p-2 w-full sm:w-48 border rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
          onChange={handleSearch}
        />
      </label>
    </div>

    {/* Export Options */}
    <div className="flex flex-wrap justify-center sm:justify-start items-center gap-4 sm:gap-6 md:gap-8 px-2 sm:px-4 w-full sm:w-auto">
      <button
        className="bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold text-sm sm:text-base py-1 sm:py-2 px-2 sm:px-4 rounded-full shadow-lg hover:shadow-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2"
        onClick={handleCopy}
      >
        <FaCopy className="text-sm sm:text-lg" />
        Copy
      </button>

      <button
        className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-semibold text-sm sm:text-base py-1 sm:py-2 px-2 sm:px-4 rounded-full shadow-lg hover:shadow-xl hover:from-yellow-600 hover:to-yellow-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2"
        onClick={handleCsvExport}
      >
        <FaFileCsv className="text-sm sm:text-lg" />
        CSV
      </button>

      <button
        className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-sm sm:text-base py-1 sm:py-2 px-2 sm:px-4 rounded-full shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2"
        onClick={handleExcelExport}
      >
        <FaFileExcel className="text-sm sm:text-lg" />
        Excel
      </button>

      <button
        className="bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold text-sm sm:text-base py-1 sm:py-2 px-2 sm:px-4 rounded-full shadow-lg hover:shadow-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2"
        onClick={handlePdfExport}
      >
        <FaFilePdf className="text-sm sm:text-lg" />
        PDF
      </button>
    </div>

    {/* Entries Dropdown */}
    <div className="flex items-center w-full sm:w-auto">
      <label htmlFor="time-dropdown" className="text-lg font-medium mr-2">
        Show
        <select
          id="time-dropdown"
          value={selectedValue}
          onChange={handleChange}
          className="ml-2 p-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        entries
      </label>
    </div>
  </div>

  {/* Services Section (Responsive Cards/Table) */}
  <div className="overflow-x-auto">
    {/* Table for larger screens */}
    <table className="min-w-full table-auto border-collapse border border-gray-300 hidden sm:table">
      <thead>
        <tr className="bg-gray-100">
          <th className="px-6 py-3 text-sm font-medium text-gray-900 border-b border-gray-200">Service ID</th>
          <th className="px-6 py-3 text-sm font-medium text-gray-900 border-b border-gray-200">Service Title</th>
          <th className="px-6 py-3 text-sm font-medium text-gray-900 border-b border-gray-200">Description</th>
          <th className="px-6 py-3 text-sm font-medium text-gray-900 border-b border-gray-200">More</th>
        </tr>
      </thead>
      <tbody>
        {paginatedServices.map((service, index) => (
          <tr key={index} className="border-t border-gray-200">
            <td className="px-6 py-3 text-sm text-gray-900 border-b border-gray-200">{service.service_id}</td>
            <td className="px-6 py-3 text-sm text-gray-900 border-b border-gray-200">{service.service_title}</td>
            <td className="px-6 py-3 text-sm text-gray-900 border-b border-gray-200">{service.service_description}</td>
            <td className="px-6 py-3 text-sm text-gray-900 border-b border-gray-200">
              <div className="flex space-x-2">
                <button
                  className="text-blue-500 flex items-center gap-2 p-2 border rounded-md hover:bg-blue-100"
                  onClick={() => handleAddonsClick(service)}
                >
                  Addons
                </button>
                <button
                  className="text-yellow-500 flex items-center gap-2 p-2 border rounded-md hover:bg-yellow-100"
                  onClick={() => handleMethodsClick(service)}
                >
                  Method
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>

    {/* Card View for mobile devices */}
    <div className="sm:hidden overflow-x-auto">
  {/* Grid layout for mobile (with horizontal scroll enabled) */}
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 w-full">
    {paginatedServices.map((service, index) => (
      <div key={index} className="bg-white border rounded-md p-4 shadow-lg w-full sm:w-full md:w-60 lg:w-60">
        {/* Service ID */}
        <div className="mb-2">
          <strong className="text-gray-700">Service ID: </strong>
          <span>{service.service_id}</span>
        </div>

        {/* Service Title */}
        <div className="mb-2">
          <strong className="text-gray-700">Service Title: </strong>
          <span>{service.service_title}</span>
        </div>

        {/* Service Description */}
        <div className="mb-4">
          <strong className="text-gray-700">Description: </strong>
          <p className="line-clamp-3">{service.service_description}</p>
          <button
            className="text-blue-500 mt-2"
            onClick={() => toggleMore(service.service_id)}
          >
            More
          </button>
        </div>

        {/* Action Buttons (Addons, Method) */}
        <div className="flex justify-between items-center">
          <button
            className="text-blue-500 flex items-center gap-2 p-2 border rounded-md hover:bg-blue-100"
            onClick={() => handleAddonsClick(service)}
          >
            Addons
          </button>
          <button
            className="text-yellow-500 flex items-center gap-2 p-2 border rounded-md hover:bg-yellow-100"
            onClick={() => handleMethodsClick(service)}
          >
            Method
          </button>
        </div>
      </div>
    ))}
  </div>
</div>

  </div>
</div>

      {/* Addons Modal */}
      {showAddonsModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg w-3/4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Addons for {selectedService?.service_title}</h2>
              <button onClick={closeAddonsModal} className="text-red-500">
                <FaTimesCircle />
              </button>
            </div>

            <div className="overflow-x-auto mt-4">
              <table className="min-w-full table-auto border-collapse border border-gray-300">
                <thead>
                  <tr>
                    <th className="p-3 border text-left">Addon ID</th>
                    <th className="p-3 border text-left">Addon Name</th>
                    <th className="p-3 border text-left">Addon Price</th>
                    <th className="p-3 border text-left">Max Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedAddons.map((addon, index) => (
                    <tr key={index}>
                      <td className="p-3 border">{addon.addon_id}</td>
                      <td className="p-3 border">{addon.addon_name}</td>
                      <td className="p-3 border">{addon.addon_price}</td>
                      <td className="p-3 border">{addon.addon_maxqty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Methods Modal */}
      {showMethodsModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg w-3/4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Methods for {selectedService?.service_title}</h2>
              <button onClick={closeMethodsModal} className="text-red-500">
                <FaTimesCircle />
              </button>
            </div>

            <div className="overflow-x-auto mt-4">
              <table className="min-w-full table-auto border-collapse border border-gray-300">
                <thead>
                  <tr>
                    <th className="p-3 border text-left">Method ID</th>
                    <th className="p-3 border text-left">Method Title</th>
                    <th className="p-3 border text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedMethods.map((method, index) => (
                    <tr key={index}>
                      <td className="p-3 border">{method.method_id}</td>
                      <td className="p-3 border">{method.method_title}</td>
                      <td className="p-3 border">{method.method_status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {entriesPerPage !== "All" && (
        <div className="flex items-center  justify-end mt-3">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-gray-300 rounded-l hover:bg-gray-400 disabled:opacity-50"
          >
            Previous
          </button>
          {[...Array(totalPages).keys()].map((page) => (
            <button
              key={page + 1}
              onClick={() => handlePageChange(page + 1)}
              className={`px-4 py-2 border ${
                currentPage === page + 1
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 hover:bg-gray-300"
              }`}
            >
              {page + 1}
            </button>
          ))}
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-gray-300 rounded-r hover:bg-gray-400 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
    
  </div>
  );
};

export default Serviceinformation ;
